import axios from 'axios'
import { useStore } from '../store/authStore'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5212/api'

export const publicAssetUrl = (path?: string | null, cacheKey?: string | number | null) => {
  if (!path) return ''
  const raw = path.startsWith('http://') || path.startsWith('https://')
    ? path
    : `${API_URL.replace(/\/api\/?$/, '').replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`
  if (cacheKey == null || cacheKey === '') return raw
  return `${raw}${raw.includes('?') ? '&' : '?'}v=${encodeURIComponent(String(cacheKey))}`
}

export function extractApiError(error: unknown, fallback: string): string {
  const err = error as {
    response?: {
      status?: number
      statusText?: string
      data?: { message?: string; Message?: string; errors?: string[]; Errors?: string[]; title?: string }
    }
    message?: string
  }
  const data = err?.response?.data
  const msg = data?.message ?? data?.Message ?? data?.title
  if (typeof msg === 'string' && msg.trim()) return msg

  const errors = data?.errors ?? data?.Errors
  if (Array.isArray(errors) && errors.length) return errors.filter(Boolean).join(' — ')

  const status = err?.response?.status
  if (status === 404) return 'العملية غير متاحة على السيرفر — تأكد من نشر نسخة API الأحدث'
  if (status === 403) return 'غير مصرح — هذه العملية متاحة لمالك النظام (Owner) فقط'
  if (status === 413) return 'حجم الملف كبير جداً (الحد الأقصى 10 MB)'
  if (status === 401) return 'انتهت الجلسة — سجّل الدخول مجدداً'
  if (status === 415) return 'نوع الطلب غير مدعوم — أعد تحميل الصفحة وجرب مرة أخرى'
  if (status && status >= 500) return `خطأ من السيرفر (${status}) — تحقق من مجلد license-agreements وصلاحيات الكتابة`
  if (!err?.response) return 'تعذر الاتصال بالخادم — تحقق من الإنترنت أو API'
  return fallback
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

let refreshPromise: Promise<string | null> | null = null

const redirectToLogin = () => {
  useStore.getState().logout()
  if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
    window.location.assign('/login')
  }
}

/** تجديد الجلسة (مشترك بين checkAuth والـ interceptor). */
export const ensureSession = async (): Promise<boolean> => {
  const { refreshToken } = useStore.getState()
  if (!refreshToken) {
    useStore.getState().logout()
    return false
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken: useStore.getState().refreshToken })
      const payload = response?.data?.data ?? response?.data
      const newToken = payload?.accessToken ?? payload?.token
      const newRefreshToken = payload?.refreshToken

      if (!newToken || !newRefreshToken) {
        throw new Error('Invalid refresh response')
      }

      useStore.setState({
        token: newToken,
        refreshToken: newRefreshToken,
        user: payload?.user ?? useStore.getState().user,
        isAuthenticated: true,
      })
      return newToken as string
    })().finally(() => {
      refreshPromise = null
    })
  }

  try {
    const token = await refreshPromise
    return !!token
  } catch {
    redirectToLogin()
    return false
  }
}

api.interceptors.request.use(
  (config) => {
    const { token } = useStore.getState()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type']
    }
    return config
  },
  (error) => Promise.reject(error),
)

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const url = String(originalRequest?.url || '')
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh')

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      try {
        const ok = await ensureSession()
        if (!ok) return Promise.reject(error)

        const newToken = useStore.getState().token
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      } catch {
        redirectToLogin()
        return Promise.reject(error)
      }
    }

    if (error.response?.status === 401 && isAuthEndpoint) {
      redirectToLogin()
    }

    return Promise.reject(error)
  },
)

// تعريفات API
export const authService = {
  login: async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password })
    return response.data
  },

  refreshToken: async (refreshToken: string) => {
    const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken })
    return response.data
  },

  logout: async () => {
    const { refreshToken } = useStore.getState()
    const response = await api.post('/auth/logout', { refreshToken: refreshToken || '' })
    return response.data
  },
}

export const userService = {
  getAll: async () => {
    const response = await api.get('/users')
    return response.data
  },

  getById: async (id: string) => {
    const response = await api.get(`/users/${id}`)
    return response.data
  },

  create: async (data: any) => {
    const response = await api.post('/users', data)
    return response.data
  },

  update: async (id: string, data: any) => {
    const response = await api.put(`/users/${id}`, data)
    return response.data
  },

  delete: async (id: string) => {
    const response = await api.delete(`/users/${id}`)
    return response.data
  },

  getProfile: async () => {
    const response = await api.get('/users/profile')
    return response.data
  },

  updateProfile: async (data: any) => {
    const response = await api.put('/users/profile', data)
    return response.data
  },

  changePassword: async (data: any) => {
    const response = await api.post('/users/change-password', data)
    return response.data
  },
}

export const dashboardService = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats')
    return response.data
  },
}

export const errorLogService = {
  getRecent: async (take = 50) => {
    const response = await api.get('/errorlogs/recent', { params: { take } })
    return response.data
  },

  getByCustomer: async (customerId: string, take = 50) => {
    const response = await api.get(`/errorlogs/customer/${customerId}`, { params: { take } })
    return response.data
  },

  markFixed: async (errorId: number, data?: any) => {
    const response = await api.put(`/errorlogs/${errorId}/fix`, data ?? {})
    return response.data
  },

  markReported: async (errorId: number, data?: any) => {
    const response = await api.put(`/errorlogs/${errorId}/report`, data ?? {})
    return response.data
  },

  delete: async (errorId: number) => {
    const response = await api.delete(`/errorlogs/${errorId}`)
    return response.data
  },

  deleteMany: async (errorIds: number[]) => {
    const response = await api.post('/errorlogs/delete-many', { errorIds })
    return response.data
  },

  clearFixed: async () => {
    const response = await api.post('/errorlogs/clear-fixed')
    return response.data
  },
}

export const systemUpdateService = {
  getLatest: async (currentVersion?: string) => {
    const response = await api.get('/systemupdates/latest', { params: { currentVersion } })
    return response.data
  },

  getAll: async () => {
    const response = await api.get('/systemupdates')
    return response.data
  },

  createRelease: async (data: any) => {
    const response = await api.post('/systemupdates', data)
    return response.data
  },

  uploadPackage: async (
    id: string,
    file: File,
    product: string = 'WinApp',
    onProgress?: (percent: number) => void,
  ) => {
    const { token } = useStore.getState()
    const base = API_URL.replace(/\/api\/?$/, '')
    const url = `${base}/api/systemupdates/${id}/package?product=${encodeURIComponent(product)}`
    return new Promise<any>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url)
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100))
      }
      xhr.onload = () => {
        let data: any = {}
        try { data = JSON.parse(xhr.responseText || '{}') } catch { data = { message: xhr.responseText } }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data)
        else reject({ response: { status: xhr.status, data } })
      }
      xhr.onerror = () => reject({ response: null })
      const form = new FormData()
      form.append('file', file)
      xhr.send(form)
    })
  },

  deleteRelease: async (id: string) => {
    const response = await api.delete(`/systemupdates/${id}`)
    return response.data
  },

  getEnrollmentBoard: async (id: string) => {
    const response = await api.get(`/systemupdates/${id}/devices`)
    return response.data
  },

  addCustomers: async (id: string, customerIds: string[]) => {
    const response = await api.post(`/systemupdates/${id}/customers`, { customerIds })
    return response.data
  },

  removeEnrollment: async (id: string, deviceId: string) => {
    const response = await api.delete(`/systemupdates/${id}/devices/${deviceId}`)
    return response.data
  },

  markDeviceUpdated: async (id: string, deviceId: string) => {
    const response = await api.post(`/systemupdates/${id}/devices/${deviceId}/mark-updated`)
    return response.data
  },
}

export const deviceService = {
  getAll: async (params?: any) => {
    const response = await api.get('/devices', { params })
    return response.data
  },

  getById: async (id: string) => {
    const response = await api.get(`/devices/${id}`)
    return response.data
  },

  create: async (data: any) => {
    const response = await api.post('/devices', data)
    return response.data
  },

  update: async (id: string, data: any) => {
    const response = await api.put(`/devices/${id}`, data)
    return response.data
  },

  updateSiteSettings: async (id: string, data: {
    meraApiUrl?: string | null
    useIndependentReportBranding?: boolean
    reportHeaderHeightMm?: number
    reportFooterHeightMm?: number
    receiptHeaderHeightMm?: number
    dbPasswordOverride?: string | null
  }) => {
    const response = await api.put(`/devices/${id}/site-settings`, data)
    return response.data
  },

  setUpdatePending: async (id: string, pending = true, includeLinkedApi = false, releaseId?: string) => {
    const params = new URLSearchParams()
    params.set('pending', String(pending))
    params.set('includeLinkedApi', String(includeLinkedApi))
    if (releaseId) params.set('releaseId', releaseId)
    const response = await api.post(`/devices/${id}/update-pending?${params.toString()}`)
    return response.data
  },

  uploadReportImage: async (id: string, kind: 'header' | 'footer' | 'receiptHeader', file: File) => {
    const form = new FormData()
    form.append('file', file)
    const response = await api.post(`/devices/${id}/report-images?kind=${encodeURIComponent(kind)}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  deleteReportImage: async (id: string, kind: 'header' | 'footer' | 'receiptHeader') => {
    const response = await api.delete(`/devices/${id}/report-images`, { params: { kind } })
    return response.data
  },

  renewSubscription: async (id: string, data: any) => {
    const response = await api.post(`/devices/${id}/renew-subscription`, data)
    return response.data
  },

  changeSubscription: async (id: string, data: any) => {
    const response = await api.post(`/devices/${id}/change-subscription`, data)
    return response.data
  },

  resetActivation: async (id: string) => {
    const response = await api.post(`/devices/${id}/reset-activation`)
    return response.data
  },

  delete: async (id: string) => {
    const response = await api.delete(`/devices/${id}`)
    return response.data
  },

  activate: async (data: any) => {
    const response = await api.post('/devices/activate', data)
    return response.data
  },
}

export const serviceVisitService = {
  getAll: async (params?: any) => {
    const response = await api.get('/servicevisits', { params })
    return response.data
  },

  getById: async (id: string) => {
    const response = await api.get(`/servicevisits/${id}`)
    return response.data
  },

  create: async (data: any) => {
    const response = await api.post('/servicevisits', data)
    return response.data
  },

  update: async (id: string, data: any) => {
    const response = await api.put(`/servicevisits/${id}`, data)
    return response.data
  },

  delete: async (id: string) => {
    const response = await api.delete(`/servicevisits/${id}`)
    return response.data
  },

  completeVisit: async (id: string, data: any) => {
    const response = await api.post(`/servicevisits/${id}/complete`, data)
    return response.data
  },
}

export const customerService = {
  getAll: async (params?: any) => {
    const response = await api.get('/customers', { params })
    return response.data
  },

  getById: async (id: string) => {
    const response = await api.get(`/customers/${id}`)
    return response.data
  },

  getWithDevices: async (id: string) => {
    const response = await api.get(`/customers/${id}/devices`)
    return response.data
  },

  create: async (data: any) => {
    const response = await api.post('/customers', data)
    return response.data
  },

  update: async (id: string, data: any) => {
    const response = await api.put(`/customers/${id}`, data)
    return response.data
  },

  delete: async (id: string) => {
    const response = await api.delete(`/customers/${id}`)
    return response.data
  },

  uploadLicenseAgreement: async (
    id: string,
    file: File,
    onProgress?: (percent: number) => void
  ) => {
    const { token } = useStore.getState()
    const base = API_URL.replace(/\/api\/?$/, '').replace(/\/$/, '')
    const url = `${base}/api/customers/${id}/license-agreement`

    return new Promise<unknown>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url)
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      xhr.timeout = 120000

      xhr.upload.onprogress = (event) => {
        if (onProgress && event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100))
        } else if (onProgress) {
          onProgress(0)
        }
      }

      xhr.onload = () => {
        let data: { message?: string; Message?: string; success?: boolean } = {}
        try {
          data = JSON.parse(xhr.responseText)
        } catch {
          data = { message: xhr.responseText?.slice(0, 200) || `خطأ ${xhr.status}` }
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data)
        } else {
          reject({ response: { status: xhr.status, data } })
        }
      }

      xhr.onerror = () => reject({ response: null })
      xhr.ontimeout = () => reject({ response: { status: 408, data: { message: 'انتهت مهلة الرفع — حاول مجدداً' } } })

      const form = new FormData()
      form.append('file', file)
      xhr.send(form)
    })
  },

  downloadLicenseAgreement: async (id: string, customerName?: string) => {
    const { token } = useStore.getState()
    const base = API_URL.replace(/\/api\/?$/, '')
    const url = `${base}/api/customers/${id}/license-agreement`
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('تعذر تحميل اتفاقية الترخيص')
    const blob = await response.blob()
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `اتفاقية-ترخيص-${customerName || 'customer'}.pdf`
    link.click()
    URL.revokeObjectURL(link.href)
  },
}

export const siteSettingsService = {
  get: async () => {
    const response = await api.get('/sitesettings')
    return response.data
  },
  update: async (defaultDbPassword: string) => {
    const response = await api.put('/sitesettings', { defaultDbPassword })
    return response.data
  },
}

export const customerReportBrandingService = {
  update: async (
    id: string,
    body: {
      ownerName?: string
      specialization?: string
      reportHeaderHeightMm: number
      reportFooterHeightMm: number
      receiptHeaderHeightMm: number
    }
  ) => {
    const response = await api.put(`/customers/${id}/report-branding`, body)
    return response.data
  },
  uploadImage: async (id: string, kind: 'header' | 'footer' | 'receiptHeader', file: File) => {
    const form = new FormData()
    form.append('file', file)
    const response = await api.post(`/customers/${id}/report-images?kind=${encodeURIComponent(kind)}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
  deleteImage: async (id: string, kind: 'header' | 'footer' | 'receiptHeader') => {
    const response = await api.delete(`/customers/${id}/report-images`, { params: { kind } })
    return response.data
  },
}

export const backupService = {
  getLatest: async () => {
    const response = await api.get('/backup')
    return response.data
  },

  create: async () => {
    const response = await api.post('/backup')
    return response.data
  },

  download: (fileName: string) => {
    const { token } = useStore.getState()
    const base = API_URL.replace(/\/api\/?$/, '')
    const url = `${base}/api/backup/download/${encodeURIComponent(fileName)}`
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    if (token) {
      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.blob())
        .then((blob) => {
          link.href = URL.createObjectURL(blob)
          link.click()
        })
        .catch(() => window.open(url, '_blank'))
      return
    }
    link.click()
  },

  restoreLatest: async () => {
    const response = await api.post('/backup/restore')
    return response.data
  },

  restoreUpload: async (file: File) => {
    const form = new FormData()
    form.append('file', file)
    const response = await api.post('/backup/restore-upload', form)
    return response.data
  },
}

export default api
